import React, { useState, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { functionService } from "../../lib/functions";
import { Select, DatePicker, Dropdown, Menu, Button, Modal, Tooltip, Input, message } from "antd";
import {
  CopyOutlined,
  DownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import ProfileComp from "../profileView/ProfileComp";
import { Switch } from "antd";

/* ─────────────────────────────────────────────
   Helper: truncate text with max length
───────────────────────────────────────────── */
const truncateText = (text, maxLength = 25) => {
  if (!text) return "-";
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/* ─────────────────────────────────────────────
   Helper: faceted/constraint-based dropdown options.
   Given the raw filterOptions rows, returns the unique
   sorted values for `targetKey`, filtered by every
   constraint in `constraints` EXCEPT the target's own
   current selection (constraints intentionally omits it).
   Same helper used in ProfileSentTable.
───────────────────────────────────────────── */
const getFacetOptions = (rows, targetKey, constraints) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const filtered = rows.filter((row) =>
    Object.entries(constraints).every(([key, value]) => {
      if (!value) return true; // no constraint selected for this key
      return row[key] === value;
    })
  );
  return [...new Set(filtered.map((row) => row[targetKey]).filter(Boolean))].sort();
};

/* ─────────────────────────────────────────────
   Shared pagination styles
───────────────────────────────────────────── */
const paginationStyles = {
  container: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 4px", borderTop: "1px solid #F0F0F0", marginTop: "8px",
  },
  leftSection: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#6B7280" },
  pageControls: { display: "flex", alignItems: "center", gap: "4px" },
  pageBtn: (active) => ({
    minWidth: "34px", height: "34px", padding: "0 10px", borderRadius: "8px",
    border: active ? "none" : "1px solid #E5E7EB",
    background: active ? "#4F46E5" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#374151",
    fontWeight: active ? 600 : 400, fontSize: "13px", cursor: "pointer",
    transition: "all 0.15s ease", display: "flex", alignItems: "center", justifyContent: "center",
    boxShadow: active ? "0 1px 4px rgba(79,70,229,0.3)" : "none",
  }),
  ellipsis: {
    width: "34px", height: "34px", display: "flex", alignItems: "center",
    justifyContent: "center", color: "#9CA3AF", fontSize: "14px", userSelect: "none",
  },
  navBtn: (disabled) => ({
    height: "34px", padding: "0 14px", borderRadius: "8px", border: "1px solid #E5E7EB",
    background: disabled ? "#F9FAFB" : "#FFFFFF", color: disabled ? "#D1D5DB" : "#374151",
    fontWeight: 500, fontSize: "13px", cursor: disabled ? "not-allowed" : "pointer",
    display: "flex", alignItems: "center", gap: "4px", transition: "all 0.15s ease",
  }),
};

/* ─────────────────────────────────────────────
   Reusable status badge helpers — same as ProfileSentTable
───────────────────────────────────────────── */
const PendingBadge = () => (
  <span style={{ color: "#b45309", fontWeight: 600, background: "#fef3c7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    Pending
  </span>
);

const YesBadge = () => (
  <span style={{ color: "#166534", fontWeight: 600, background: "#dcfce7", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    Yes
  </span>
);

const NoBadge = () => (
  <span style={{ color: "#991b1b", fontWeight: 600, background: "#fee2e2", padding: "2px 8px", borderRadius: "4px", fontSize: "12px" }}>
    No
  </span>
);

/* ─────────────────────────────────────────────
   Candidate status options
───────────────────────────────────────────── */
const candidateStatusOptions = [
  "Yet to be shortlisted",
  "Screen Reject",
  "Pre-Interview Back Out",
  "Post Interview Back Out",

  // R1
  "Screen Select - Yet to be Interviewed (R1)",
  "Interview Scheduled (R1)",
  "During Interview Back Out (R1)",
  "Interviewed - Feedback Pending (R1)",
  "Interviewed - Rejected (R1)",

  // R2
  "Yet to be Interviewed (R2)",
  "Interview Scheduled (R2)",
  "During Interview Back Out (R2)",
  "Interviewed - Feedback Pending (R2)",
  "Interviewed - Rejected (R2)",

  // R3
  "Yet to be Interviewed (R3)",
  "Interview Scheduled (R3)",
  "During Interview Back Out (R3)",
  "Interviewed - Feedback Pending (R3)",
  "Interviewed - Rejected (R3)",

  // R4
  "Yet to be Interviewed (R4)",
  "Interview Scheduled (R4)",
  "During Interview Back Out (R4)",
  "Interviewed - Feedback Pending (R4)",
  "Interviewed - Rejected (R4)",

  // Final Stage
  "Final Select",
  "Final Reject",
  "Pre Offer Back Out",

  // Offer Stage
  "Offer released",
  "Offer yet to be released",
  "Post Offer Back Out",

  // Joining Stage
  "Offer Accepted - Yet to join",
  "Offer Accepted - Joined",
  "Offer Accepted - Not Joined",
  "Offer Rejected - Not joined",

  // Other Outcomes
  "Position on hold/closed",
  "Change in requirement from client",
  "Duplicate Profile",
  "Profile Reshared"
];

const ReviewerTable = () => {
  const [jobListings, setJobListings] = useState([]);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCompanyShortlistStatus, setSelectedCompanyShortlistStatus] = useState(null);
  const [selectedAssessmentStatus, setSelectedAssessmentStatus] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSourcedDate, setSelectedSourcedDate] = useState(null);
  const [selectedCandidateStatus, setSelectedCandidateStatus] = useState(null); // NEW
  const [filteredJobListings, setFilteredJobListings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedAction, setSelectedAction] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [modalData, setModalData] = useState({ id: "", email: "", firstName: "", lastName: "" });
  const [viewData, setViewData] = useState(null);
  const [selectedSubAccount, setSelectedSubAccount] = useState(null);
  const [selectedEndClient, setSelectedEndClient] = useState(null);

  // NOTE: companyOptions / roleList / subAccountOptions / endClientOptions
  // are no longer stored in state. They are derived below via useMemo from
  // `filterOptions` + the OTHER selected filters (faceted/constraint-based
  // filtering), same pattern as ProfileSentTable, so they can never go
  // stale and never need manual syncing against table data.
  const [filterOptions, setFilterOptions] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [reviewerRemark, setReviewerRemark] = useState("");
  const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showOnlyApproved, setShowOnlyApproved] = useState(false);
  const [showOnlyRejected, setShowOnlyRejected] = useState(false);
  const [selectedProfileSent, setSelectedProfileSent] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [generatingScoreId, setGeneratingScoreId] = useState(null);

  const [isCompactViewHovered, setIsCompactViewHovered] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);
  const [activeRecruitersOnlyHovered, setActiveRecruitersOnlyHovered] = useState(false);
  const [activeRecruitersOnly, setActiveRecruitersOnly] = useState(true);
  const [selectedDebriefStatus, setSelectedDebriefStatus] = useState(null);
  const [selectedDebriefDate, setSelectedDebriefDate] = useState(null);

  const [dateRange, setDateRange] = useState([]);
  const { RangePicker } = DatePicker;

  const getCompanyName = async () => {
    const response = await functionService.get("demand/demand-companies-list");
    if (response?.response?.data?.data?.length > 0) {
      setCompanyList(response.response.data.data);
    }
  };

  const recruiterSearch = async () => {
    try {
      let res = await functionService.get(`demand/recruiters-list`);
      let apiData = res?.response?.data?.data;
      const optionsWithNames = apiData.map((item) => ({ ...item, recruiter_name: item.label }));
      setRecruiterOptions(optionsWithNames);
    } catch (error) {
      console.error("Error fetching recruiters list:", error);
    }
  };
  const getFilterOptions = async () => {
    try {
      const response = await functionService.get(
        "demand/demand-filter-list"
      );

      if (response?.response?.data?.data) {
        setFilterOptions(response.response.data.data);
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  /* ─────────────────────────────────────────────
     Faceted/constraint-based dropdown options.

     Each dropdown's valid values are computed from
     `filterOptions` constrained by the OTHER three
     currently selected filters — never by its own
     current selection. Whenever any one of the four
     filters changes, all four recompute automatically
     because they're plain derived values (useMemo),
     not separately-managed state synced off table rows.
  ───────────────────────────────────────────── */
  const companyOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "company_name", {
        role_title: selectedRole,
        sub_account: selectedSubAccount,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedRole, selectedSubAccount, selectedEndClient]
  );

  const roleList = useMemo(
    () =>
      getFacetOptions(filterOptions, "role_title", {
        company_name: selectedCompany,
        sub_account: selectedSubAccount,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedCompany, selectedSubAccount, selectedEndClient]
  );

  const subAccountOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "sub_account", {
        company_name: selectedCompany,
        role_title: selectedRole,
        end_client: selectedEndClient,
      }),
    [filterOptions, selectedCompany, selectedRole, selectedEndClient]
  );

  const endClientOptions = useMemo(
    () =>
      getFacetOptions(filterOptions, "end_client", {
        company_name: selectedCompany,
        role_title: selectedRole,
        sub_account: selectedSubAccount,
      }),
    [filterOptions, selectedCompany, selectedRole, selectedSubAccount]
  );

  /* ─────────────────────────────────────────────
     Stale-selection handling.

     If a selected value is no longer present in its
     OWN valid option list (because a sibling filter
     changed and narrowed things down), clear just that
     one selection — never blindly clear the others.
     Each effect is keyed off the derived options array,
     so it only fires when that dropdown's valid set
     actually changes.
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (selectedCompany && !companyOptions.includes(selectedCompany)) {
      setSelectedCompany(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyOptions]);

  useEffect(() => {
    if (selectedRole && !roleList.includes(selectedRole)) {
      setSelectedRole(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleList]);

  useEffect(() => {
    if (selectedSubAccount && !subAccountOptions.includes(selectedSubAccount)) {
      setSelectedSubAccount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subAccountOptions]);

  useEffect(() => {
    if (selectedEndClient && !endClientOptions.includes(selectedEndClient)) {
      setSelectedEndClient(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endClientOptions]);

  const fetchProfileListings = async (page = 1, activeFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: itemsPerPage });
      Object.entries(activeFilters).forEach(([key, val]) => {
        if (val !== null && val !== undefined && val !== "") {
          params.append(key, val);
        }
      });

      const filters = { page, limit: itemsPerPage, ...activeFilters };
      const demandDetails = await functionService.getProfileListings(filters);
      if (demandDetails?.status) {
        const responseData = demandDetails.response.data.data;
        // handle both { data: [], total: n } and direct array
        const listings = Array.isArray(responseData) ? responseData : (responseData?.data ?? []);
        const total = responseData?.total ?? listings.length;

        setJobListings(listings);
        setFilteredJobListings(listings);
        setTotalCount(total);
      }
    } catch (error) {
      console.error("Error in fetchDetails:", error);
    } finally {
      setLoading(false);
    }
  };
  // 1. Initial load
  useEffect(() => {
    getCompanyName();
    recruiterSearch();
    getFilterOptions();
    fetchProfileListings(1, {});
  }, []);

  // 2. Build filters helper
  const buildFilters = () => ({
    ...(candidateSearch && { candidate_search: candidateSearch }),
    ...(selectedCompany && { company_name: selectedCompany }),
    ...(selectedRole && { role_title: selectedRole }),
    ...(selectedRecruiter && { recruiter_name: selectedRecruiter }),
    ...(selectedSubAccount && { sub_account: selectedSubAccount }),
    ...(selectedEndClient && { end_client: selectedEndClient }),
    ...(selectedAssessmentStatus && { assessment_status: selectedAssessmentStatus }),
    ...(selectedCandidateStatus && { candidate_current_status: selectedCandidateStatus }),
    ...(selectedProfileSent && { profile_sent: selectedProfileSent }),
    ...(selectedSourcedDate && { sourced_date: selectedSourcedDate.format("YYYY-MM-DD") }),
    ...(selectedDate && { reviewed_date: selectedDate.format("YYYY-MM-DD") }),
    ...(selectedDebriefStatus && {
      debrief_status: selectedDebriefStatus,
    }),
    ...(selectedDebriefDate && {
      debrief_date: selectedDebriefDate.format("YYYY-MM-DD"),
    }),
    ...(showOnlyPending && { review_status: "pending" }),
    ...(showOnlyApproved && { review_status: "approved" }),
    ...(showOnlyRejected && { review_status: "rejected" }),
    ...(dateRange?.length === 2 && {
      sourced_date_from: dateRange[0].format("YYYY-MM-DD"),
      sourced_date_to: dateRange[1].format("YYYY-MM-DD"),
    }),
    // Always sent now — two-state filter (active vs inactive), no
    // longer an optional "show everyone" state.
    recruiter_active_filter: activeRecruitersOnly ? "active" : "inactive",
  });

  // 3. Filters changed → debounced fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProfileListings(1, buildFilters());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [
    candidateSearch,
    selectedCompany, selectedRole, selectedRecruiter, selectedSubAccount,
    selectedEndClient, selectedAssessmentStatus, selectedCandidateStatus,
    selectedProfileSent, selectedSourcedDate, selectedDate,
    selectedDebriefStatus, selectedDebriefDate,
    showOnlyPending, showOnlyApproved, showOnlyRejected, dateRange, activeRecruitersOnly,
  ]);

  // 4. Page or page size changed
  useEffect(() => {
    fetchProfileListings(currentPage, buildFilters());
  }, [currentPage, itemsPerPage]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedListings = Array.isArray(filteredJobListings) ? filteredJobListings : [];
  const showEmptyState = paginatedListings.length === 0;

  const buildPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (currentPage > 3) pages.push("...");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++)
      pages.push(i);
    if (currentPage < totalPages - 2) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  const generateAiScore = async (item) => {
    setGeneratingScoreId(item.id);
    try {
      const response = await functionService.post(`demand/generate-ai-score/${item.id}`, {});
      if (response?.status) {
        await fetchProfileListings(currentPage, buildFilters());
      } else {
        console.error("Error generating AI score:", response?.error);
        alert("Failed to generate AI score. Please try again.");
      }
    } catch (error) {
      console.error("Error generating AI score:", error);
      alert("Failed to generate AI score. Please try again.");
    } finally {
      setGeneratingScoreId(null);
    }
  };

  const downloadAssessmentReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}` // ← removed ?candidate_id=
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}assessment_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading assessment report:", error);
      alert("Failed to download report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };
  const downloadCVAndReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}?candidate_id=${item.id}` // ← same as old downloadAssessmentReport
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}cv_and_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading CV & report:", error);
      alert("Failed to download CV & report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };
  const downloadKyndrylReport = async (item) => {
    setDownloadingId(item.assessment_id);
    try {
      const response = await functionService.getReportWithResume(
        `demand/download-assessment-report/${item.assessment_id}?report_type=half`
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = `${item.first_name ? item.first_name + "_" : ""}${item.last_name ? item.last_name + "_" : ""}kyndryl_assessment_report`.replace(/\./g, "");
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Kyndryl report:", error);
      alert("Failed to download Kyndryl report. Please try again.");
    } finally {
      setDownloadingId(null);
    }
  };
  const menu = (item) => (
    <Menu onClick={(e) => handleMenuClick(e, item)}>
      {item.assessment_status?.toUpperCase() === "SUBMITTED" && (
        <>
          <Menu.Item key="download" disabled={downloadingId === item.assessment_id}>
            {downloadingId === item.assessment_id ? "Downloading..." : "Download Assessment Report"}
          </Menu.Item>
          <Menu.Item key="downloadHalf" disabled={downloadingId === item.assessment_id}>
            {downloadingId === item.assessment_id ? "Downloading..." : "Kyndryl Assessment Report"}
          </Menu.Item>
          <Menu.Item key="downloadCVAndReport" disabled={downloadingId === item.assessment_id}>  {/* ← NEW */}
            {downloadingId === item.assessment_id ? "Downloading..." : "Download CV & Report"}
          </Menu.Item>
        </>
      )}
      {localStorage.getItem("adminAccess") <= 1 && (
        <>
          {item.review_status !== "approved" && <Menu.Item key="approved">Approve</Menu.Item>}
          {item.review_status !== "approved" && <Menu.Item key="rejected">Reject</Menu.Item>}
        </>
      )}
    </Menu>
  );

  const handleMenuClick = (e, item) => {
    setUserId(item.id);
    switch (e.key) {
      case "download":
        downloadAssessmentReport(item);
        break;
      case "downloadHalf":
        downloadKyndrylReport(item);
        break;
      case "downloadCVAndReport":       // ← NEW
        downloadCVAndReport(item);
        break;
      case "approved":
      case "rejected":
        setSelectedAction(e.key);
        setIsRemarkModalVisible(true);
        break;
      default:
        break;
    }
  };

  const updateStatusWithRemark = async () => {
    if (selectedAction === "approved" || selectedAction === "rejected") {
      try {
        const candidateStatus = await functionService.updateCandidateStatus(userId, {
          status: selectedAction, remark: reviewerRemark,
        });
        if (candidateStatus.status) {
          await fetchProfileListings(
            currentPage,
            buildFilters()
          );
        }
        else { console.error("Error updating candidate status:", candidateStatus.error); }
      } catch (error) { console.error("Error in updateStatusWithRemark:", error); }
    }
    setIsRemarkModalVisible(false);
    setReviewerRemark("");
  };

  const handleRowSelect = (row) => {
    setSelectedRows((prev) => prev.includes(row) ? prev.filter((r) => r !== row) : [...prev, row]);
  };

  const handleSelectAll = (isChecked) => { setSelectedRows(isChecked ? paginatedListings : []); };
  const copyDebriefToClipboard = (debriefText) => {
    if (!debriefText) {
      message.error("No debrief text to copy");
      return;
    }
    navigator.clipboard
      .writeText(debriefText)
      .then(() => {
        message.success("Debrief copied successfully");
      })
      .catch(() => {
        message.error("Failed to copy");
      });
  };


  const handleCancel = () => { setIsModalVisible(false); setIsViewModalVisible(false); };

  const profile = {
    name: `${viewData?.first_name} ${viewData?.last_name}`, contact: viewData?.primary_no,
    email: viewData?.email,
    exp: `${viewData?.total_work_exp / 12} years ${viewData?.total_work_exp % 12} month`,
    salary: viewData?.current_ctc, score: 92,
    imageUrl: "https://media.istockphoto.com/id/513727339/photo/head-and-shoulders-mans-silhouette.jpg?s=612x612&w=0&k=20&c=aAVL1IT9GT4URtLjS98515G5e3Qc4c7Np2EvKXBywMQ=",
    cvUrl: viewData?.cv_url,
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not available";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "Invalid date"; }
  };

  const SkillScores = ({ techScore, assessmentId }) => {
    const [open, setOpen] = React.useState(false);
    const [skills, setSkills] = React.useState(null);  // null = not fetched yet
    const [fetching, setFetching] = React.useState(false);
    const ref = React.useRef(null);

    // Close on outside click
    React.useEffect(() => {
      if (!open) return;
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleOpen = async () => {
      const next = !open;
      setOpen(next);

      // Only fetch once
      if (next && skills === null && assessmentId) {
        setFetching(true);
        try {
          const res = await functionService.getSkillScore(assessmentId);
          if (res?.status) {
            const skillData = res.response.data?.data ?? res.response.data ?? [];
            setSkills(Array.isArray(skillData) ? skillData : []);
          } else {
            setSkills([]);
          }
        } catch {
          setSkills([]);
        } finally {
          setFetching(false);
        }
      }
    };

    if (!assessmentId) {
      return <span>{techScore != null ? `${techScore}%` : "-"}</span>;
    }

    const barColor = (score) =>
      score >= 65 ? "#1D9E75" : score >= 40 ? "#EF9F27" : "#E24B4A";

    const truncate = (text, max = 22) =>
      text?.length > max ? text.substring(0, max) + "…" : text;

    return (
      <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
        <div
          onClick={handleOpen}
          style={{
            display: "flex", alignItems: "center", gap: "5px",
            cursor: "pointer", userSelect: "none",
            padding: "3px 7px", borderRadius: "6px",
            background: open ? "#F0F0FF" : "#F9FAFB",
            border: "1px solid #E5E7EB",
            transition: "background 0.15s",
          }}
        >
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{techScore}%</span>
          <span style={{ fontSize: "9px", color: "#9CA3AF" }}>{open ? "▲" : "▼"}</span>
        </div>

        {open && (
          <div
            style={{
              position: "fixed", zIndex: 9999,
              background: "#fff", border: "1px solid #E5E7EB",
              borderRadius: "10px", padding: "10px 12px",
              width: "230px", boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            }}
            ref={(el) => {
              if (!el) return;

              const trigger = ref.current?.firstChild;
              if (!trigger) return;

              const rect = trigger.getBoundingClientRect();

              const popupHeight = 240;
              const spaceBelow = window.innerHeight - rect.bottom;

              // open upward if not enough space below
              const shouldOpenUp = spaceBelow < popupHeight;

              el.style.left = `${rect.left}px`;

              if (shouldOpenUp) {
                el.style.top = `${rect.top - popupHeight + 36}px`;
              } else {
                el.style.top = `${rect.bottom + 6}px`;
              }
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: "8px", paddingBottom: "6px", borderBottom: "1px solid #F3F4F6"
            }}>
              <span style={{ fontSize: "12px", fontWeight: 600, color: "#374151" }}>Skill breakdown</span>
            </div>

            {/* Loading state */}
            {fetching && (
              <div style={{ fontSize: "12px", color: "#9CA3AF", textAlign: "center", padding: "8px 0" }}>
                Loading...
              </div>
            )}

            {/* Skill list */}
            {!fetching && skills?.length > 0 && (
              <div style={{ maxHeight: "180px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "7px" }}>
                {skills.map((s, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}>
                      <span style={{ color: "#6B7280" }}>{truncate(s.skill_name)}</span>
                      <span style={{ fontWeight: 600, color: "#111827", minWidth: "28px", textAlign: "right" }}>{s.score}%</span>
                    </div>
                    <div style={{ height: "4px", background: "#F3F4F6", borderRadius: "2px", overflow: "hidden" }}>
                      <div style={{
                        width: `${s.score}%`, height: "100%",
                        background: barColor(s.score), borderRadius: "2px",
                        transition: "width 0.3s ease",
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state */}
            {!fetching && skills?.length === 0 && (
              <div style={{ fontSize: "12px", color: "#9CA3AF", textAlign: "center", padding: "8px 0" }}>
                No skill data available
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const parsePgArray = (val) => {
    if (!val) return "-";
    if (Array.isArray(val)) return val.join(", ");
    try {
      const cleaned = val.replace(/^{|}$/g, ""); // strip { }
      if (!cleaned) return "-";
      const items = cleaned
        .split(",")
        .map((s) => s.trim().replace(/^"|"$/g, "")) // strip surrounding quotes
        .filter(Boolean);
      return items.length ? items.join(", ") : "-";
    } catch {
      return "-";
    }
  };

  // Formats a min/max CTC range with the "Upto X LPA" fallback when only max is set
  const formatCtcRange = (min, max) => {
    const hasMin = min !== null && min !== undefined && min !== "";
    const hasMax = max !== null && max !== undefined && max !== "";
    if (hasMin && hasMax) return `${min} - ${max} LPA`;
    if (!hasMin && hasMax) return `Upto ${max} LPA`;
    if (hasMin && !hasMax) return `${min}+ LPA`;
    return "-";
  };

  // Formats a min/max years range with sensible fallbacks, same pattern as formatCtcRange
  const formatYearsRange = (min, max) => {
    const hasMin = min !== null && min !== undefined && min !== "";
    const hasMax = max !== null && max !== undefined && max !== "";
    if (hasMin && hasMax) return `${min} - ${max} yrs`;
    if (!hasMin && hasMax) return `Upto ${max} yrs`;
    if (hasMin && !hasMax) return `${min}+ yrs`;
    return "-";
  };

  const AiScoreDetail = ({ aiScoreData, candidate }) => {
    const [open, setOpen] = React.useState(false);
    const [coords, setCoords] = React.useState({ top: 0, left: 0, openUp: false });
    const triggerRef = React.useRef(null);
    const popupRef = React.useRef(null);

    const POPUP_WIDTH = 380;
    const POPUP_MAX_HEIGHT = 460;
    const MARGIN = 12;

    const computePosition = React.useCallback(() => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < POPUP_MAX_HEIGHT && spaceAbove > spaceBelow;

      let left = rect.left;
      if (left + POPUP_WIDTH + MARGIN > window.innerWidth) {
        left = window.innerWidth - POPUP_WIDTH - MARGIN;
      }
      if (left < MARGIN) left = MARGIN;

      let top;
      if (openUp) {
        top = Math.max(rect.top - Math.min(POPUP_MAX_HEIGHT, spaceAbove - MARGIN), MARGIN);
      } else {
        top = rect.bottom + 6;
      }

      setCoords({ top, left, openUp });
    }, []);

    React.useEffect(() => {
      if (!open) return;
      computePosition();

      const handleClickOutside = (e) => {
        if (
          triggerRef.current && !triggerRef.current.contains(e.target) &&
          popupRef.current && !popupRef.current.contains(e.target)
        ) {
          setOpen(false);
        }
      };
      const handleReposition = () => computePosition();

      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleReposition, true);
      window.addEventListener("resize", handleReposition);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleReposition, true);
        window.removeEventListener("resize", handleReposition);
      };
    }, [open, computePosition]);

    if (!aiScoreData) {
      return <PendingBadge />;
    }

    const report = aiScoreData.report || {};
    const score = aiScoreData.score ?? report.overall_score;
    const verdict = aiScoreData.verdict || report.recommendation;

    const verdictColor = (v) => {
      if (v === "Strong Shortlist") return { bg: "#dcfce7", color: "#166534" };
      if (v === "Shortlist") return { bg: "#dbeafe", color: "#1d4ed8" };
      if (v === "Borderline") return { bg: "#fef3c7", color: "#b45309" };
      if (v === "Reject") return { bg: "#fee2e2", color: "#991b1b" };
      if (v === "Strong Reject") return { bg: "#fee2e2", color: "#7f1d1d" };
      return { bg: "#F3F4F6", color: "#374151" };
    };

    const scoreColor = (s) => (s >= 70 ? "#1D9E75" : s >= 55 ? "#EF9F27" : "#E24B4A");
    const vc = verdictColor(verdict);

    const popup = (
      <div
        ref={popupRef}
        style={{
          position: "fixed",
          zIndex: 9999,
          top: coords.top,
          left: coords.left,
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "12px",
          padding: "16px 18px",
          width: `${POPUP_WIDTH}px`,
          maxHeight: `${POPUP_MAX_HEIGHT}px`,
          overflowY: "auto",
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          boxSizing: "border-box",
        }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: "12px", paddingBottom: "10px", borderBottom: "1px solid #F3F4F6"
        }}>
          <span style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>AI Match Report</span>
          <span style={{
            fontSize: "11px", fontWeight: 600, padding: "3px 10px", borderRadius: "999px",
            background: vc.bg, color: vc.color, whiteSpace: "nowrap",
          }}>
            {verdict}
          </span>
        </div>

        {Array.isArray(report.skills) && report.skills.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "8px", letterSpacing: "0.03em" }}>
              MUST-HAVE SKILLS
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {report.skills.map((s, i) => (
                <div key={i} style={{
                  borderBottom: i !== report.skills.length - 1 ? "1px solid #F3F4F6" : "none",
                  paddingBottom: "10px",
                }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                    fontSize: "12.5px", gap: "8px",
                  }}>
                    <span style={{ fontWeight: 600, color: "#111827", wordBreak: "break-word" }}>{s.skill}</span>
                    <span style={{
                      fontWeight: 600, color: scoreColor(s.score), flexShrink: 0,
                      whiteSpace: "nowrap", fontSize: "11px",
                    }}>
                      {s.status}
                    </span>
                  </div>
                  <div style={{
                    fontSize: "11.5px", color: "#6B7280", marginTop: "4px",
                    lineHeight: 1.5, wordBreak: "break-word", overflowWrap: "break-word",
                  }}>
                    {s.evidence}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {report.experience_summary && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "8px", letterSpacing: "0.03em" }}>
              EXPERIENCE SUMMARY
            </div>
            <div style={{ fontSize: "12.5px", color: "#374151", lineHeight: 1.7 }}>
              <div><strong>Total:</strong> {report.experience_summary.total_experience || "-"}</div>
              <div><strong>Relevant:</strong> {report.experience_summary.relevant_experience || "-"}</div>
              <div><strong>Domain:</strong> {report.experience_summary.domain_relevance || "-"}</div>
            </div>
          </div>
        )}

        {report.job_stability?.companies?.length > 0 && (
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "8px", letterSpacing: "0.03em" }}>
              JOB STABILITY
            </div>
            {report.job_stability.companies.map((c, i) => (
              <div key={i} style={{ fontSize: "12.5px", color: "#374151", marginBottom: "3px", wordBreak: "break-word" }}>
                {c.company} — {c.tenure_months} mo. <span style={{ color: "#9CA3AF" }}>({c.remark})</span>
              </div>
            ))}
            <div style={{ fontSize: "11.5px", color: "#6B7280", marginTop: "6px" }}>
              Avg tenure: {report.job_stability.average_tenure_months} mo. ·{" "}
              Frequent switching: {report.job_stability.frequent_switching_flag ? "Yes" : "No"}
            </div>
          </div>
        )}

        {Array.isArray(report.risk_flags) && report.risk_flags.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#991b1b", marginBottom: "8px", letterSpacing: "0.03em" }}>
              RISK FLAGS
            </div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {report.risk_flags.map((flag, i) => (
                <li key={i} style={{ fontSize: "11.5px", color: "#7f1d1d", marginBottom: "4px", wordBreak: "break-word" }}>
                  {flag}
                </li>
              ))}
            </ul>
          </div>
        )}

        {report.hard_gate_applied?.applied && (
          <div style={{
            fontSize: "11.5px", color: "#92400e", background: "#fef3c7",
            padding: "8px 10px", borderRadius: "8px", marginTop: "8px", wordBreak: "break-word",
          }}>
            Score capped: {report.hard_gate_applied.reason}
          </div>
        )}
        {/* Demand Requirements — from the job/demand side */}
        {candidate && (
          <div style={{
            marginTop: "14px", paddingTop: "12px", borderTop: "1px solid #F3F4F6",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "8px", letterSpacing: "0.03em" }}>
              DEMAND REQUIREMENTS
            </div>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px",
            }}>
              <div>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Notice Period</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {candidate.demand_notice_period || "-"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Experience Required</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {formatYearsRange(candidate.demand_min_yrs, candidate.demand_max_yrs)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Budget (CTC)</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {formatCtcRange(candidate.demand_min_ctc, candidate.demand_max_ctc)}
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Location</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827", wordBreak: "break-word" }}>
                  {parsePgArray(candidate.demand_location)}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Candidate Snapshot — Notice Period, Salary, Location, Experience */}
        {candidate && (
          <div style={{
            marginTop: "14px", paddingTop: "12px", borderTop: "1px solid #F3F4F6",
          }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "8px", letterSpacing: "0.03em" }}>
              CANDIDATE SNAPSHOT
            </div>
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 16px",
            }}>
              <div>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Notice Period</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {(() => {
                    if (candidate.notice_period) {
                      return candidate.notice_period;
                    }
                    if (candidate.last_working_day) {
                      const lwd = new Date(candidate.last_working_day);
                      const today = new Date();
                      // normalize both to midnight so we're comparing whole days
                      lwd.setHours(0, 0, 0, 0);
                      today.setHours(0, 0, 0, 0);
                      const diffDays = Math.round((lwd - today) / (1000 * 60 * 60 * 24));

                      if (diffDays > 0) {
                        return `Available In ${diffDays} day${diffDays === 1 ? "" : "s"}`;
                      }
                      return `Available From ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"}`;
                    }
                    return "-";
                  })()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Experience</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  {candidate.total_work_exp ? `${candidate.total_work_exp}y` : "-"}
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Salary</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827" }}>
                  CTC - {candidate.current_ctc ? `${candidate.current_ctc} LPA` : "-"}
                  <span style={{ color: "#D1D5DB", margin: "0 8px" }}>|</span>
                  Asked For - {candidate.expected_ctc ? `${candidate.expected_ctc} LPA` : "-"}
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginBottom: "2px" }}>Current Location</div>
                <div style={{ fontSize: "12.5px", fontWeight: 600, color: "#111827", wordBreak: "break-word" }}>
                  {candidate.candidate_location || "-"}
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ fontSize: "10.5px", color: "#9CA3AF", marginTop: "12px" }}>
          Generated: {aiScoreData.generated_at ? formatDate(aiScoreData.generated_at) : "-"}
        </div>
      </div>
    );

    return (
      <>
        <div
          ref={triggerRef}
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "flex", alignItems: "center", gap: "5px",
            cursor: "pointer", userSelect: "none",
            padding: "3px 7px", borderRadius: "6px",
            background: open ? "#F0F0FF" : "#F9FAFB",
            border: "1px solid #E5E7EB",
            transition: "background 0.15s",
            width: "fit-content",
          }}
        >
          <span style={{ fontSize: "13px", fontWeight: 600, color: scoreColor(score) }}>{score}%</span>
          <span style={{ fontSize: "9px", color: "#9CA3AF" }}>{open ? "▲" : "▼"}</span>
        </div>

        {open && ReactDOM.createPortal(popup, document.body)}
      </>
    );
  };

  const exportToCSV = async () => {
    let baseData = selectedRows.length > 0 ? selectedRows : [];

    // If no rows are manually selected, fetch ALL records
    // matching the currently applied filters.
    if (selectedRows.length === 0) {
      try {
        const result = await functionService.getProfileListings({
          ...buildFilters(),
          page: 1,
          limit: 100000,
        });

        if (result.status) {
          baseData = result.response?.data?.data?.data || [];
        } else {
          console.error("Failed to fetch all data for export");
          return;
        }
      } catch (error) {
        console.error("Error exporting CSV:", error);
        return;
      }
    }

    const headers = [
      "Review Status",
      "Date of Sourcing",
      "Date of Review",
      "Recruiter Name",
      "Account Name",
      "Role",
      "Candidate Name",
      "Candidate Email ID",
      "Candidate Mobile Number",
      "Total Exp",
      "Relevant Exp",
      "Current Company",
      "Current Location",
      "Preferred Location",
      "Current CTC",
      "Expected CTC",
      "Notice Period",
      "Notice Status",
      "Offer in Hand (Y/N)",
      "Offered CTC",
      "Joining Date of Offer",
      "Date of Birth",
      "Highest Qualification",
      "Recruiter's Remarks",
      "Reviewer's Remarks",
      "Assessment Status",
      "Tech Score",
      "Red Flag",
      "Qualify",
      "Profile Sent",
      "Date of Sharing with Client",
      "Assessment Schedule",
      "Candidate Current Status",
      "Client's Feedback",
      "Client's Remark",
      "Debrief Form",
      "Resume Link",
    ];

    const csvRows = [headers.join(",")];

    baseData.forEach((item) => {
      const escape = (val) => {
        const str =
          val === null || val === undefined ? "-" : String(val);

        return str.includes(",") ||
          str.includes('"') ||
          str.includes("\n")
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };

      const row = [
        escape(
          item.review_status
            ? item.review_status.charAt(0).toUpperCase() +
            item.review_status.slice(1)
            : "-"
        ),

        escape(formatDate(item.created_at)),

        escape(
          item.review_status === "pending"
            ? "Pending..."
            : formatDate(item.updated_at)
        ),

        escape(item.recruiter_name || "-"),
        escape(item.company_name || "-"),
        escape(item.role_title || "-"),

        escape(
          `${item.first_name || ""} ${item.last_name || ""}`.trim() || "-"
        ),

        escape(item.email || "-"),
        escape(item.primary_no || "-"),

        escape(
          item.total_work_exp
            ? `${item.total_work_exp}y`
            : "-"
        ),

        escape(
          item.relevant_experience
            ? `${item.relevant_experience}y`
            : "-"
        ),

        escape(item.current_company || "-"),
        escape(item.candidate_location || "-"),

        escape(
          Array.isArray(item.candidate_prefered_location)
            ? item.candidate_prefered_location.join(", ")
            : item.candidate_prefered_location || "-"
        ),

        escape(
          item.current_ctc
            ? `${item.current_ctc} LPA`
            : "-"
        ),

        escape(
          item.expected_ctc
            ? `${item.expected_ctc} LPA`
            : "-"
        ),

        escape(
          item.notice_period
            ? `${item.notice_period} Days`
            : "-"
        ),

        escape(item.notice_status || "-"),

        escape(item.offer_in_hand ? "Yes" : "No"),

        escape(
          item.offer_in_hand && item.offered_ctc
            ? `${item.offered_ctc} LPA`
            : "-"
        ),

        escape(
          item.offer_in_hand && item.joining_date_of_offer
            ? formatDate(item.joining_date_of_offer)
            : "-"
        ),

        escape(
          item.date_of_birth
            ? formatDate(item.date_of_birth)
            : "-"
        ),

        escape(item.highest_qualification || "-"),
        escape(item.remarks || "-"),
        escape(item.reviewer_remark || "-"),

        escape(
          item.assessment_status
            ? "Attempted"
            : "Not Attempted"
        ),

        escape(
          item.assessment_status
            ? `${item.tech_score}%`
            : "-"
        ),

        escape(
          item.assessment_status
            ? item.tech_score >= 65
              ? "No"
              : "Yes"
            : "-"
        ),

        escape(
          item.assessment_status
            ? item.tech_score >= 65
              ? "Yes"
              : "No"
            : "-"
        ),

        escape(
          item.profile_sent === null ||
            item.profile_sent === undefined
            ? "Pending"
            : item.profile_sent
              ? "Yes"
              : "No"
        ),

        escape(
          item.profile_sent_date
            ? formatDate(item.profile_sent_date)
            : item.profile_sent === null ||
              item.profile_sent === undefined
              ? "Pending"
              : "-"
        ),

        escape(
          item.assessment_schedule_datetime
            ? dayjs(item.assessment_schedule_datetime).format(
              "DD-MM-YYYY HH:mm"
            )
            : "Pending"
        ),

        escape(item.candidate_current_status || "Pending"),

        escape(item.client_feedback || "-"),

        escape(item.client_remark || "-"),

        escape(
          (() => {
            let debrief = item.debrief_form;

            if (typeof debrief === "string") {
              try {
                debrief = JSON.parse(debrief);
              } catch (e) {
                return "-";
              }
            }

            if (
              !Array.isArray(debrief) ||
              debrief.length === 0
            ) {
              return "-";
            }

            return [...debrief]
              .sort((a, b) => {
                const getOrder = (name = "") => {
                  const match = name.match(/\d+/);

                  return match
                    ? Number(match[0])
                    : Number.MAX_SAFE_INTEGER;
                };

                return (
                  getOrder(a.round_name) -
                  getOrder(b.round_name)
                );
              })
              .map((round) => {
                const questions = round.questions?.length
                  ? round.questions.join(" | ")
                  : "-";

                return `${round.round_name || "-"}: ${questions}${round.additional_information
                    ? ` | Additional Info: ${round.additional_information}`
                    : ""
                  }`;
              })
              .join(" || ");
          })()
        ),

        escape(item.cv_url || "-"),
      ];

      csvRows.push(row.join(","));
    });

    const blob = new Blob(
      [csvRows.join("\n")],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `reviewer_table_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);

    setSelectedRows([]);
  };

  const MIN_DATE = dayjs("2026-04-22").startOf("day");
  const disableFutureDates = (current) =>
    current && (current > dayjs().endOf("day") || current < MIN_DATE);

  const compactViewShowBlue = isCompactViewHovered ? !isCompactView : isCompactView;
  const activeRecruitersShowBlue = activeRecruitersOnlyHovered ? !activeRecruitersOnly : activeRecruitersOnly;
  return (
    <Styled.MainDiv>
      <Sidemenu />
      <Styled.ContentWrapDiv>
        <Styled.TopContainer>
          <div className="leftSide" style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span
              style={{
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
              }}
            >
              Reviewer's Profiles
            </span>
            {/* Active Recruiters Only */}
            <Tooltip title={activeRecruitersOnly ? "Showing active recruiters" : "Showing all recruiters"}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                }}
                onClick={() => setActiveRecruitersOnly(!activeRecruitersOnly)}
                onMouseEnter={() => setActiveRecruitersOnlyHovered(true)}
                onMouseLeave={() => setActiveRecruitersOnlyHovered(false)}
              >
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: activeRecruitersShowBlue ? "#1B49EA" : "#374151",
                    whiteSpace: "nowrap",
                    transition: "color 0.15s ease",
                  }}
                >
                  {activeRecruitersOnly}
                </span>

                <div
                  style={{
                    width: "44px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Switch
                    checked={activeRecruitersOnly}
                    onChange={setActiveRecruitersOnly}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "44px",
                      minWidth: "44px",
                      maxWidth: "44px",
                      height: "22px",
                      minHeight: "22px",
                      background: activeRecruitersOnly ? "#1677ff" : "#D1D5DB",
                      transition: "background 0.15s ease",
                    }}
                  />
                </div>
              </div>
            </Tooltip>
          </div>
          <div className="rightSide" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Compact View */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                padding: "8px 12px",
                background: compactViewShowBlue ? "#E8F0FF" : "#F9FAFB",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }}
              onClick={() => setIsCompactView(!isCompactView)}
              onMouseEnter={() => setIsCompactViewHovered(true)}
              onMouseLeave={() => setIsCompactViewHovered(false)}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: compactViewShowBlue ? "#1B49EA" : "#374151",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease",
                }}
              >
                Compact View
              </span>

              <div
                style={{
                  width: "44px",
                  height: "22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Switch
                  checked={isCompactView}
                  onChange={setIsCompactView}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "44px",
                    minWidth: "44px",
                    maxWidth: "44px",
                    height: "22px",
                    minHeight: "22px",
                    background: compactViewShowBlue ? "#1677ff" : "#D1D5DB",
                    transition: "background 0.15s ease",
                  }}
                />
              </div>
            </div>


            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlyPending ? "#fef3c7" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>

              <input
                type="checkbox"
                checked={showOnlyPending}
                onChange={(e) => {
                  setShowOnlyPending(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlyApproved(false);
                    setShowOnlyRejected(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlyPending ? "#b45309" : "#374151" }}>
                {showOnlyPending ? "Showing Pending" : "Pending"}
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlyApproved ? "#dcfce7" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <input
                type="checkbox"
                checked={showOnlyApproved}
                onChange={(e) => {
                  setShowOnlyApproved(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlyPending(false);
                    setShowOnlyRejected(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlyApproved ? "#166534" : "#374151" }}>
                {showOnlyApproved ? "Showing Approved" : "Approved"}
              </span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "8px 12px", background: showOnlyRejected ? "#fee2e2" : "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <input
                type="checkbox"
                checked={showOnlyRejected}
                onChange={(e) => {
                  setShowOnlyRejected(e.target.checked);
                  if (e.target.checked) {
                    setShowOnlyPending(false);
                    setShowOnlyApproved(false);
                  }
                }}
                style={{ width: "18px", height: "18px", cursor: "pointer", accentColor: "#4F46E5" }}
              />
              <span style={{ fontSize: "14px", fontWeight: 500, color: showOnlyRejected ? "#991b1b" : "#374151" }}>
                {showOnlyRejected ? "Showing Rejected" : "Rejected"}
              </span>
            </label>
            <button onClick={exportToCSV}>Export</button>
          </div>
        </Styled.TopContainer>

        {/* ── Filters ── */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "20px",
            overflowX: "auto",
            flexWrap: "nowrap",
            paddingBottom: "6px",
            scrollbarWidth: "thin",
            scrollbarColor: "#D1D5DB transparent",
          }}
        >
          <Select showSearch placeholder="Select a company" onChange={(value) => setSelectedCompany(value)}
            allowClear style={{ minWidth: "180px", flexShrink: 0 }} value={selectedCompany}>
            {companyOptions.map((company) => (
              <Select.Option
                key={company}
                value={company}
              >
                {company}
              </Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="Select a role"
            onChange={(value) => setSelectedRole(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedRole}
          >
            {roleList.map((role) => (
              <Select.Option key={role} value={role}>
                <Tooltip title={role} placement="top">
                  <span style={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle"
                  }}>
                    {role}
                  </span>
                </Tooltip>
              </Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="Sub-account"
            onChange={(value) => setSelectedSubAccount(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedSubAccount}
          >
            {subAccountOptions.map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>

          <Select
            showSearch
            placeholder="End client"
            onChange={(value) => setSelectedEndClient(value)}
            allowClear
            style={{ minWidth: "180px", flexShrink: 0 }}
            value={selectedEndClient}
          >
            {endClientOptions.map((e) => (
              <Select.Option key={e} value={e}>{e}</Select.Option>
            ))}
          </Select>

          <Select placeholder="Assessment status" onChange={(value) => setSelectedAssessmentStatus(value)}
            allowClear style={{ minWidth: "160px", flexShrink: 0 }} value={selectedAssessmentStatus}>
            <Select.Option value="Attempted">Attempted</Select.Option>
            <Select.Option value="Not Attempted">Not Attempted</Select.Option>
          </Select>

          <Select showSearch placeholder="Select a recruiter" optionFilterProp="children"
            onChange={(value) => setSelectedRecruiter(value)} allowClear style={{ minWidth: "180px", flexShrink: 0 }} value={selectedRecruiter}>
            {recruiterOptions
              .filter((item) => !activeRecruitersOnly || item.active === true)
              .map((item) => (
                <Select.Option key={item.recruiter_name} value={item.recruiter_name}>{item.recruiter_name}</Select.Option>
              ))}
          </Select>
          <Select
            placeholder="Profile Sent"
            onChange={(value) => setSelectedProfileSent(value)}
            allowClear
            style={{ minWidth: "150px", flexShrink: 0 }}
            value={selectedProfileSent}
          >
            <Select.Option value="pending">Pending</Select.Option>
            <Select.Option value="yes">Yes</Select.Option>
            <Select.Option value="no">No</Select.Option>
          </Select>

          <Input
            placeholder="Search Name / Email"
            value={candidateSearch}
            onChange={(e) => setCandidateSearch(e.target.value)}
            allowClear
            style={{
              maxWidth: "220px",
              flexShrink: 0
            }}
          />

          {/* <DatePicker placeholder="Sourced Date" onChange={(date) => setSelectedSourcedDate(date || null)}
            format="DD MMM YYYY" allowClear disabledDate={disableFutureDates} style={{ width: "200px", flexShrink: 0 }} /> */}

          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates || []);
            }}
            format="DD MMM YYYY"
            placeholder={["Sourced From", "Sourced To"]}
            allowClear
            disabledDate={disableFutureDates}
            separator={
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "24px",
                  minWidth: "24px",
                  flexShrink: 0,
                  fontSize: "16px",
                  lineHeight: "1",
                }}
              >
                →
              </span>
            }
            style={{
              width: "262px",
              minWidth: "262px",
              flexShrink: 0,
            }}
          />

          <DatePicker placeholder="Reviewed Date" onChange={(date) => setSelectedDate(date || null)}
            format="DD MMM YYYY" allowClear disabledDate={disableFutureDates} style={{ width: "200px", flexShrink: 0 }} />

          <Select
            placeholder="Debrief Status"
            value={selectedDebriefStatus}
            onChange={(value) => setSelectedDebriefStatus(value)}
            allowClear
            style={{ minWidth: "160px", flexShrink: 0 }}
          >
            <Select.Option value="Filled">Filled</Select.Option>
            <Select.Option value="Not Filled">Not Filled</Select.Option>
          </Select>

          <DatePicker
            placeholder="Debrief Date"
            value={selectedDebriefDate}
            onChange={(date) => setSelectedDebriefDate(date || null)}
            format="DD MMM YYYY"
            allowClear
            disabledDate={disableFutureDates}
            style={{ width: "200px", flexShrink: 0 }}
          />

          <Select placeholder="Candidate Status" onChange={(value) => setSelectedCandidateStatus(value)}
            allowClear showSearch style={{ minWidth: "200px", flexShrink: 0 }} value={selectedCandidateStatus}>
            {candidateStatusOptions.map((status) => (
              <Select.Option key={status} value={status}>{status}</Select.Option>
            ))}
          </Select>
        </div>

        <Styled.TableWrap style={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 260px)" }}>
          {showEmptyState ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", marginTop: "100px" }}>
              <img src={require("../../images/Frame.png")} alt="No Data" style={{ width: "35%", height: "auto" }} />
              <h2 style={{ marginTop: "auto" }}>No Result Found</h2>
            </div>
          ) : (
            <Styled.MainTable style={{ transition: "background-color 0.3s ease-in-out" }}>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  background: "#F2F6F8",
                }}
              >
                <tr>
                  {/* Always visible checkbox */}
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      zIndex: 11,
                      background: "#F2F6F8",
                    }}
                  >
                    <input
                      type="checkbox"
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      checked={
                        selectedRows.length === paginatedListings.length &&
                        paginatedListings.length > 0
                      }
                    />
                  </th>

                  {isCompactView ? (
                    <>
                      {/* COMPACT VIEW */}

                      <th
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Review Status
                      </th>

                      <th
                        style={{
                          position: "sticky",
                          left: "50px",
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Candidate Details
                      </th>

                      <th
                        style={{
                          position: "sticky",
                          left: "150px",
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Date of Sourcing
                      </th>

                      <th>Account Name</th>

                      <th>Role</th>

                      <th>Recruiter Name</th>

                      <th>Candidate Current Status</th>

                      <th>Client Remarks</th>

                      <th>Reviewer Remark</th>

                      <th>Recruiter Remark</th>

                      <th>AI Score</th>

                      <th>Tech Score</th>

                      <th>Actions</th>
                    </>
                  ) : (
                    <>
                      {/* DETAILED VIEW */}

                      <th
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Review status
                      </th>

                      <th
                        style={{
                          position: "sticky",
                          left: "50px",
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Date of Sourcing
                      </th>

                      <th
                        style={{
                          position: "sticky",
                          left: "100px",
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Date of Review
                      </th>

                      <th
                        style={{
                          position: "sticky",
                          left: "150px",
                          zIndex: 11,
                          background: "#F2F6F8",
                        }}
                      >
                        Candidate details
                      </th>

                      <th>Date of Sharing with Client</th>
                      <th>Profile Sent</th>
                      <th>Candidate Current Status</th>
                      <th>Client's Feedback</th>
                      <th>Client's Remark</th>
                      <th>Assessment Schedule</th>
                      <th>Recruiter Name</th>
                      <th>Account Name</th>
                      <th>Role</th>
                      <th>Total Exp</th>
                      <th>Relevant Exp</th>
                      <th>Current Company</th>
                      <th>Current Location</th>
                      <th>Preferred Location</th>
                      <th>Current CTC</th>
                      <th>Expected CTC</th>
                      <th>Notice Period</th>
                      <th>Notice Status</th>
                      <th>Last Working Date</th>
                      <th>Available From</th>
                      <th>Offer in Hand (Y/N)</th>
                      <th>Joining Date of Offer</th>
                      <th>Date of Birth</th>
                      <th>Highest Qualification</th>
                      <th>Recruiter's Remarks</th>
                      <th>Reviewer's Remarks</th>
                      <th>Fitment Score</th>
                      <th>AI Score</th>
                      <th>Assessment status</th>
                      <th>Tech Score</th>
                      <th>Red Flag</th>
                      <th>Qualify</th>
                      <th>Debrief View</th>
                      <th>Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedListings.map((item, index) => {
                  const rowBg = selectedRows.includes(item)
                    ? "#d0e7ff"
                    : "#ffffff";
                  if (isCompactView) {
                    return (
                      <tr
                        key={index}
                        style={{
                          background: rowBg,
                          transition: "background-color 0.3s ease-in-out",
                        }}
                      >
                        {/* Checkbox */}
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            zIndex: 1,
                            background: rowBg,
                            minWidth: "40px",
                          }}
                        >
                          <input
                            type="checkbox"
                            onChange={() => handleRowSelect(item)}
                            checked={selectedRows.includes(item)}
                          />
                        </td>

                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            background:
                              selectedRows.includes(item) && item.review_status === "pending"
                                ? "#d0e7ff"
                                : item.review_status === "approved"
                                  ? "lightgreen"
                                  : item.review_status === "rejected"
                                    ? "lightcoral"
                                    : "white",
                            zIndex: 1,
                            padding: "5px 10px",
                            borderRadius: "5px",
                            color: "black",
                          }}
                        >
                          {item.review_status
                            ? item.review_status.charAt(0).toUpperCase() +
                            item.review_status.slice(1)
                            : "-"}
                        </td>

                        {/* Candidate Details */}
                        <td
                          style={{
                            position: "sticky",
                            left: "50px",
                            backgroundColor: selectedRows.includes(item)
                              ? "#d0e7ff"
                              : "white",
                            transition: "background-color 0.2s ease-in-out",
                            zIndex: 1,
                            minWidth: "200px",
                          }}
                        >
                          {item.first_name} {item.last_name}
                          <br />
                          {item.email}
                          <br />
                          {item.primary_no}
                        </td>

                        {/* Date of Sourcing */}
                        <td
                          style={{
                            position: "sticky",
                            left: "150px",
                            backgroundColor: selectedRows.includes(item)
                              ? "#d0e7ff"
                              : "white",
                            transition: "background-color 0.2s ease-in-out",
                            zIndex: 1,
                          }}
                        >
                          {item.created_at
                            ? new Date(item.created_at).toLocaleDateString(undefined, {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : "-"}
                        </td>

                        {/* Account Name */}
                        <td>{item.company_name || "-"}</td>

                        {/* Role */}
                        <td>{item.role_title || "-"}</td>

                        {/* Recruiter Name */}
                        <td>{item.recruiter_name || "-"}</td>

                        {/* Candidate Current Status */}
                        <td>
                          {item.candidate_current_status ? (
                            item.candidate_current_status
                          ) : (
                            <PendingBadge />
                          )}
                        </td>

                        {/* Client Remarks */}
                        <td>
                          <Tooltip
                            title={item.client_remark || "-"}
                            placement="topLeft"
                          >
                            <span
                              style={{
                                maxWidth: "150px",
                                display: "inline-block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                              }}
                            >
                              {truncateText(item.client_remark, 25)}
                            </span>
                          </Tooltip>
                        </td>

                        {/* Reviewer Remark */}
                        <td>
                          <Tooltip
                            title={item.reviewer_remark || "-"}
                            placement="topLeft"
                          >
                            <span
                              style={{
                                maxWidth: "150px",
                                display: "inline-block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                              }}
                            >
                              {truncateText(item.reviewer_remark, 25)}
                            </span>
                          </Tooltip>
                        </td>

                        {/* Recruiter Remark */}
                        <td>
                          <Tooltip
                            title={item.remarks || "-"}
                            placement="topLeft"
                          >
                            <span
                              style={{
                                maxWidth: "150px",
                                display: "inline-block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                              }}
                            >
                              {truncateText(item.remarks, 25)}
                            </span>
                          </Tooltip>
                        </td>

                        {/* AI Score */}
                        <td>
                          <AiScoreDetail
                            aiScoreData={item.ai_score_data}
                            candidate={item}
                          />
                        </td>

                        {/* Tech Score + Red Flag */}
                        <td>
                          {item.assessment_status?.toUpperCase() === "SUBMITTED" ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <SkillScores
                                techScore={item.tech_score}
                                assessmentId={item.assessment_id}
                              />

                              {item.red_flag > 0 && (
                                <span
                                  style={{
                                    color: "red",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {"'" + item.red_flag + "'"}
                                </span>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Actions */}
                        <td>
                          <Styled.IconDiv>
                            <Tooltip title="View Resume">
                              <Styled.ViewButton
                                onClick={() =>
                                  window.open(item.cv_url, "_blank")
                                }
                              />
                            </Tooltip>

                            {item.misc_file_url && (
                              <Tooltip title="View Miscellaneous File">
                                <Styled.MiscFileButton
                                  onClick={() =>
                                    window.open(
                                      item.misc_file_url,
                                      "_blank"
                                    )
                                  }
                                />
                              </Tooltip>
                            )}

                            {!item.ai_score_data && (
                              <Tooltip title="Generate AI Score">
                                <Styled.AiScoreButton
                                  loading={generatingScoreId === item.id}
                                  disabled={generatingScoreId === item.id}
                                  onClick={() => generateAiScore(item)}
                                />
                              </Tooltip>
                            )}

                            {!(
                              item.review_status === "approved" &&
                              item.assessment_status?.toUpperCase() !== "SUBMITTED"
                            ) && (
                                <Tooltip title="Options">
                                  <Dropdown
                                    overlay={menu(item)}
                                    trigger={["click"]}
                                  >
                                    <Styled.OptionButton />
                                  </Dropdown>
                                </Tooltip>
                              )}
                          </Styled.IconDiv>
                        </td>

                      </tr>
                    );
                  }
                  return (
                    <tr key={index}
                      style={{ backgroundColor: selectedRows.includes(item) ? "#d0e7ff" : "transparent", transition: "background-color 0.3s ease-in-out" }}>
                      <td style={{ position: "sticky", left: 0, backgroundColor: selectedRows.includes(item) ? "#d0e7ff" : "transparent", zIndex: 1 }}>
                        <input type="checkbox" onChange={() => handleRowSelect(item)} checked={selectedRows.includes(item)} />
                      </td>
                      <td style={{
                        position: "sticky", left: 0,
                        background: selectedRows.includes(item) && item.review_status === "pending" ? "#d0e7ff"
                          : item.review_status === "approved" ? "lightgreen"
                            : item.review_status === "rejected" ? "lightcoral" : "white",
                        zIndex: 1, padding: "5px 10px", borderRadius: "5px", color: "black",
                      }}>
                        {item.review_status.charAt(0).toUpperCase() + item.review_status.slice(1)}
                      </td>
                      <td style={{ position: "sticky", left: "50px", backgroundColor: selectedRows.includes(item) ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {new Date(item.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td style={{ position: "sticky", left: "100px", backgroundColor: selectedRows.includes(item) ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {item.review_status !== "pending"
                          ? new Date(item.updated_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : "Pending..."}
                      </td>
                      <td style={{ position: "sticky", left: "150px", backgroundColor: selectedRows.includes(item) ? "#d0e7ff" : "white", transition: "background-color 0.2s ease-in-out", zIndex: 1 }}>
                        {item.first_name} {item.last_name}<br />{item.email}<br />{item.primary_no}
                      </td>

                      {/* Date of Sharing — Pending when profile_sent is null & no date */}
                      <td>
                        {item.profile_sent_date
                          ? new Date(item.profile_sent_date).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                          : (item.profile_sent === null || item.profile_sent === undefined) ? <PendingBadge /> : "-"}
                      </td>

                      {/* Profile Sent — null → Pending, true → Yes badge, false → No badge */}
                      <td>
                        {item.profile_sent === null || item.profile_sent === undefined
                          ? <PendingBadge />
                          : item.profile_sent ? <YesBadge /> : <NoBadge />}
                      </td>

                      {/* Candidate Current Status — null → Pending badge */}
                      <td>
                        {item.candidate_current_status ? item.candidate_current_status : <PendingBadge />}
                      </td>

                      <td>
                        <Tooltip title={item.client_feedback || "-"} placement="topLeft">
                          <span
                            style={{
                              maxWidth: "150px",
                              display: "inline-block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {truncateText(item.client_feedback, 25)}
                          </span>
                        </Tooltip>
                      </td>

                      <td>
                        <Tooltip title={item.client_remark || "-"} placement="topLeft">
                          <span style={{
                            maxWidth: "150px",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer"
                          }}>
                            {truncateText(item.client_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>

                      {/* Assessment Schedule Date & Time — null → Pending badge */}
                      <td>
                        {item.assessment_schedule_datetime
                          ? dayjs(item.assessment_schedule_datetime).format("DD-MM-YYYY HH:mm")
                          : <PendingBadge />}
                      </td>

                      <td>{item.recruiter_name || "-"}</td>
                      <td>{item.company_name}</td>
                      <td>{item.role_title}</td>
                      <td>{item.total_work_exp ? `${item.total_work_exp}y` : "-"}</td>
                      <td>{item.relevant_experience ? `${item.relevant_experience}y` : "-"}</td>
                      <td>{item.current_company || "-"}</td>
                      <td>{item.candidate_location || "-"}</td>
                      <td>{Array.isArray(item.candidate_prefered_location) ? item.candidate_prefered_location.join(", ") : (item.candidate_prefered_location || "-")}</td>
                      <td>{item.current_ctc ? `${item.current_ctc} LPA` : "-"}</td>
                      <td>{item.expected_ctc ? `${item.expected_ctc} LPA` : "-"}</td>
                      <td>{item.notice_period ? `${item.notice_period}` : "-"}</td>
                      <td>{item.notice_status ? item.notice_status : "-"}</td>
                      <td>{item.last_working_day ? new Date(item.last_working_day).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.available_from ? new Date(item.available_from).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.offer_in_hand ? "Yes - " + item.offered_ctc + " LPA" : "No"}</td>
                      <td>{item.offer_in_hand && item.joining_date_of_offer ? new Date(item.joining_date_of_offer).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.date_of_birth ? new Date(item.date_of_birth).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}</td>
                      <td>{item.highest_qualification || "-"}</td>
                      <td>
                        <Tooltip title={item.remarks || "-"} placement="topLeft">
                          <span style={{
                            maxWidth: "150px",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer"
                          }}>
                            {truncateText(item.remarks, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip title={item.reviewer_remark || "-"} placement="topLeft">
                          <span style={{
                            maxWidth: "150px",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer"
                          }}>
                            {truncateText(item.reviewer_remark, 25)}
                          </span>
                        </Tooltip>
                      </td>
                      <td>{item.fitment_score ? `${item.fitment_score}` : "-"}</td>
                      <td><AiScoreDetail aiScoreData={item.ai_score_data} candidate={item} /></td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" ? "Submitted" : "Not Submitted"}</td>
                      <td>{item.assessment_status?.toUpperCase() === "SUBMITTED" ? <SkillScores techScore={item.tech_score} assessmentId={item.assessment_id} /> : "-"}</td>
                      <td
                        style={{
                          color: item.assessment_status?.toUpperCase() !== "SUBMITTED"
                            ? "inherit"
                            : item.red_flag > 0
                              ? "red"
                              : "green",
                          fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                          textAlign: "center",
                        }}
                      >
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.red_flag ?? 0}
                      </td>
                      <td
                        style={{
                          backgroundColor: item.assessment_status?.toUpperCase() !== "SUBMITTED"
                            ? "transparent"
                            : item.tech_score >= 65
                              ? "lightgreen"
                              : "lightcoral",
                          fontWeight: item.assessment_status?.toUpperCase() === "SUBMITTED" ? "bold" : "normal",
                          textAlign: "center",
                        }}
                      >
                        {item.assessment_status?.toUpperCase() !== "SUBMITTED" ? "-" : item.tech_score >= 65 ? "Yes" : "No"}
                      </td>
                      <td>
                        {(() => {
                          let debriefData = [];

                          try {
                            if (typeof item.debrief_form === "string") {
                              const parsed = JSON.parse(item.debrief_form);

                              if (Array.isArray(parsed)) {
                                debriefData = parsed;
                              } else if (parsed && typeof parsed === "object") {
                                debriefData = [parsed];
                              }
                            } else if (Array.isArray(item.debrief_form)) {
                              debriefData = item.debrief_form;
                            } else if (
                              item.debrief_form &&
                              typeof item.debrief_form === "object"
                            ) {
                              debriefData = [item.debrief_form];
                            }
                          } catch (error) {
                            console.error("Invalid debrief_form:", error);
                            debriefData = [];
                          }

                          const sortedDebrief = [...debriefData].sort((a, b) => {
                            const roundA = parseInt(
                              (a.round_name || "").match(/\d+/)?.[0] || "999",
                              10
                            );

                            const roundB = parseInt(
                              (b.round_name || "").match(/\d+/)?.[0] || "999",
                              10
                            );

                            return roundA - roundB;
                          });

                          // NEW: NO Debrief Flow - safely parse debrief_no_reason
                          // using the same string/array/object/error-safe approach
                          // as debrief_form above
                          let noReasonData = [];

                          try {
                            if (typeof item.debrief_no_reason === "string") {
                              const parsedNoReason = JSON.parse(item.debrief_no_reason);

                              if (Array.isArray(parsedNoReason)) {
                                noReasonData = parsedNoReason;
                              } else if (parsedNoReason && typeof parsedNoReason === "object") {
                                noReasonData = [parsedNoReason];
                              }
                            } else if (Array.isArray(item.debrief_no_reason)) {
                              noReasonData = item.debrief_no_reason;
                            } else if (
                              item.debrief_no_reason &&
                              typeof item.debrief_no_reason === "object"
                            ) {
                              noReasonData = [item.debrief_no_reason];
                            }
                          } catch (error) {
                            console.error("Invalid debrief_no_reason:", error);
                            noReasonData = [];
                          }

                          // NEW: NO Debrief Flow - extract the numeric round
                          // number regardless of "R3" vs "Round 3" formatting,
                          // reusing the same digit-extraction approach the
                          // existing sort above already relies on
                          const getRoundNumber = (roundName) =>
                            parseInt((roundName || "").match(/\d+/)?.[0] || "999", 10);

                          // NEW: NO Debrief Flow - build a combined, round-ordered
                          // list for display only. debrief_form entries always win
                          // for a given round; a NO entry is only included when
                          // that round has no YES debrief. debrief_form itself is
                          // never modified - this array is separate/display-only.
                          const combinedRounds = [
                            ...sortedDebrief.map((round) => ({
                              type: "yes",
                              roundNumber: getRoundNumber(round.round_name),
                              data: round,
                            })),
                            ...noReasonData
                              .filter(
                                (round) =>
                                  !sortedDebrief.some(
                                    (yesRound) =>
                                      getRoundNumber(yesRound.round_name) ===
                                      getRoundNumber(round.round_name),
                                  ),
                              )
                              .map((round) => ({
                                type: "no",
                                roundNumber: getRoundNumber(round.round_name),
                                data: round,
                              })),
                          ].sort((a, b) => a.roundNumber - b.roundNumber);

                          // CHANGED: early-return threshold now checks combinedRounds
                          // instead of sortedDebrief, so a round with only a NO
                          // reason still causes the button to render
                          if (combinedRounds.length === 0) {
                            return "-";
                          }

                          // EXISTING: copy text still built from sortedDebrief
                          // (YES rounds) only, unchanged
                          const debriefText = sortedDebrief
                            .map((round) => {
                              const questions = Array.isArray(round.questions)
                                ? round.questions.join("\n")
                                : round.questions || "";

                              return `${round.round_name || "Interview Round"}\n${questions}\n${round.additional_information || ""
                                }`;
                            })
                            .join("\n\n");

                          return (
                            <Dropdown
                              menu={{
                                items: [
                                  {
                                    key: "content",
                                    label: (
                                      <div
                                        style={{
                                          maxWidth: "320px",
                                          padding: "10px",
                                          position: "relative",
                                        }}
                                      >
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<CopyOutlined />}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyDebriefToClipboard(debriefText);
                                          }}
                                          style={{
                                            position: "absolute",
                                            top: "6px",
                                            right: "6px",
                                            padding: 0,
                                            width: "24px",
                                            height: "24px",
                                            minWidth: "24px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#1b49ea",
                                          }}
                                        />

                                        <div
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: "#111827",
                                            marginBottom: "8px",
                                            paddingRight: "30px",
                                          }}
                                        >
                                          Debrief Details
                                        </div>

                                        <div
                                          style={{
                                            maxHeight: "300px",
                                            overflowY: "auto",
                                            paddingRight: "4px",
                                          }}
                                        >
                                          {/* CHANGED: iterate combinedRounds instead of sortedDebrief
                                              so NO rounds are interspersed in round order */}
                                          {combinedRounds.map((entry, roundIndex) =>
                                            entry.type === "yes" ? (
                                              // EXISTING: YES round card - completely unchanged
                                              <div
                                                key={roundIndex}
                                                style={{
                                                  marginBottom: "14px",
                                                  background: "#f8fafc",
                                                  border: "1px solid #e5e7eb",
                                                  borderRadius: "10px",
                                                  padding: "12px",
                                                  boxShadow:
                                                    "0 1px 2px rgba(0,0,0,0.04)",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "10px",
                                                    borderBottom:
                                                      "1px solid #e5e7eb",
                                                    paddingBottom: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontWeight: 700,
                                                      fontSize: "13px",
                                                      color: "#1b49ea",
                                                    }}
                                                  >
                                                    {entry.data.round_name || "Interview Round"}
                                                  </div>

                                                  <div
                                                    style={{
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    {entry.data.submitted_at
                                                      ? new Date(
                                                        entry.data.submitted_at
                                                      ).toLocaleDateString()
                                                      : "No Date"}
                                                  </div>
                                                </div>

                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    color: "#6b7280",
                                                    marginBottom: "8px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                  }}
                                                >
                                                  Questions Asked
                                                </div>

                                                {Array.isArray(entry.data.questions) &&
                                                  entry.data.questions.length > 0 ? (
                                                  entry.data.questions.map(
                                                    (question, questionIndex) => (
                                                      <div
                                                        key={questionIndex}
                                                        style={{
                                                          padding: "8px 10px",
                                                          background: "#fff",
                                                          border:
                                                            "1px solid #e5e7eb",
                                                          borderRadius: "6px",
                                                          marginBottom: "6px",
                                                          fontSize: "12px",
                                                          color: "#111827",
                                                          wordBreak: "break-word",
                                                          whiteSpace: "normal",
                                                          lineHeight: "1.5",
                                                        }}
                                                      >
                                                        <strong>
                                                          {questionIndex + 1}.
                                                        </strong>{" "}
                                                        {question}
                                                      </div>
                                                    )
                                                  )
                                                ) : (
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    No questions recorded
                                                  </div>
                                                )}

                                                {entry.data.additional_information && (
                                                  <div
                                                    style={{
                                                      marginTop: "12px",
                                                      background: "#eef4ff",
                                                      borderLeft:
                                                        "4px solid #1b49ea",
                                                      borderRadius: "6px",
                                                      padding: "10px",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: "#1b49ea",
                                                        marginBottom: "4px",
                                                        textTransform: "uppercase",
                                                      }}
                                                    >
                                                      Additional Information
                                                    </div>

                                                    <div
                                                      style={{
                                                        fontSize: "12px",
                                                        color: "#374151",
                                                        lineHeight: "1.5",
                                                      }}
                                                    >
                                                      {entry.data.additional_information}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              // NEW: NO Debrief Flow - NO round card, reuses the
                                              // same card wrapper/header style as the YES card above
                                              <div
                                                key={roundIndex}
                                                style={{
                                                  marginBottom: "14px",
                                                  background: "#fef2f2",
                                                  border: "1px solid #fca5a5",
                                                  borderRadius: "10px",
                                                  padding: "12px",
                                                  boxShadow:
                                                    "0 1px 2px rgba(0,0,0,0.04)",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    marginBottom: "10px",
                                                    borderBottom:
                                                      "1px solid #fca5a5",
                                                    paddingBottom: "8px",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontWeight: 700,
                                                      fontSize: "13px",
                                                      color: "#dc2626",
                                                    }}
                                                  >
                                                    {entry.data.round_name || "Interview Round"}
                                                  </div>

                                                  <div
                                                    style={{
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    {entry.data.submitted_at
                                                      ? new Date(
                                                        entry.data.submitted_at
                                                      ).toLocaleDateString()
                                                      : "No Date"}
                                                  </div>
                                                </div>

                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    fontWeight: 700,
                                                    color: "#dc2626",
                                                    marginBottom: "6px",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                  }}
                                                >
                                                  NO
                                                </div>

                                                <div
                                                  style={{
                                                    padding: "8px 10px",
                                                    background: "#fff",
                                                    border: "1px solid #fca5a5",
                                                    borderRadius: "6px",
                                                    fontSize: "12px",
                                                    color: "#111827",
                                                    wordBreak: "break-word",
                                                    whiteSpace: "normal",
                                                    lineHeight: "1.5",
                                                  }}
                                                >
                                                  <strong>Reason:</strong>{" "}
                                                  {entry.data.reason || "No reason provided"}
                                                </div>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    ),
                                    disabled: true,
                                  },
                                ],
                              }}
                              trigger={["click"]}
                              placement="bottomRight"
                            >
                              <Button
                                type="text"
                                size="small"
                                onClick={(e) => e.preventDefault()}
                                style={{
                                  fontSize: "11px",
                                  color: "#1b49ea",
                                  padding: "2px 4px",
                                  height: "24px",
                                  minWidth: "auto",
                                  fontWeight: 500,
                                  display: "flex",
                                  alignItems: "center",
                                  border: "1px solid #1b49ea",
                                  borderRadius: "6px",
                                  background: "#fff",
                                }}
                              >
                                <span>View Debrief</span>

                                <DownOutlined
                                  style={{
                                    fontSize: "9px",
                                    display: "flex",
                                    alignItems: "center",
                                    marginLeft: "4px",
                                  }}
                                />
                              </Button>
                            </Dropdown>
                          );
                        })()}
                      </td>

                      <td>
                        <Styled.IconDiv>
                          <Tooltip title="View Resume">
                            <Styled.ViewButton onClick={() => window.open(item.cv_url, "_blank")} />
                          </Tooltip>
                          {item.misc_file_url && (
                            <Tooltip title="View Miscellaneous File">
                              <Styled.MiscFileButton
                                onClick={() =>
                                  window.open(item.misc_file_url, "_blank")
                                }
                              />
                            </Tooltip>
                          )}
                          {!item.ai_score_data && (
                            <Tooltip title="Generate AI Score">
                              <Styled.AiScoreButton
                                loading={generatingScoreId === item.id}
                                disabled={generatingScoreId === item.id}
                                onClick={() => generateAiScore(item)}
                              />
                            </Tooltip>
                          )}
                          {!(item.review_status === "approved" && item.assessment_status?.toUpperCase() !== "SUBMITTED") && (
                            <Tooltip title="Options">
                              <Dropdown overlay={menu(item)} trigger={["click"]}>
                                <Styled.OptionButton />
                              </Dropdown>
                            </Tooltip>
                          )}
                        </Styled.IconDiv>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Styled.MainTable>
          )}
        </Styled.TableWrap>

        {/* ── Pagination ── */}
        {!showEmptyState && (
          <div style={paginationStyles.container}>
            <div style={paginationStyles.leftSection}>
              <span>Show</span>
              <Styled.PaginationSelectWrapper>
                <Select defaultValue={10} size="small" dropdownMatchSelectWidth={false}
                  onChange={(value) => { setItemsPerPage(value); setCurrentPage(1); }}>
                  <Select.Option value={10}>10</Select.Option>
                  <Select.Option value={20}>20</Select.Option>
                  <Select.Option value={50}>50</Select.Option>
                </Select>
              </Styled.PaginationSelectWrapper>
              <span>per page</span>
              <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
              <span style={{ marginLeft: "12px" }}>
                {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
              </span>
            </div>
            <div style={paginationStyles.pageControls}>
              <button style={paginationStyles.navBtn(currentPage === 1)} onClick={() => setCurrentPage((p) => p - 1)} disabled={currentPage === 1}>← Prev</button>
              {buildPageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                ) : (
                  <button key={page} style={paginationStyles.pageBtn(page === currentPage)} onClick={() => setCurrentPage(page)}>{page}</button>
                )
              )}
              <button style={paginationStyles.navBtn(currentPage === totalPages)} onClick={() => setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages}>Next →</button>
            </div>
          </div>
        )}

        {/* ── Profile View Modal ── */}
        <Modal visible={isViewModalVisible} onCancel={handleCancel} footer={[<Button key="back" onClick={handleCancel}>Close</Button>]}>
          <div><ProfileComp profile={profile} /></div>
        </Modal>

        {/* ── Reviewer Remark Modal ── */}
        <Modal visible={isRemarkModalVisible} onCancel={() => setIsRemarkModalVisible(false)}
          footer={[
            <Button key="back" onClick={() => setIsRemarkModalVisible(false)}>Cancel</Button>,
            <Button key="submit" type="primary" onClick={updateStatusWithRemark}>Submit</Button>,
          ]}
        >
          <h3>Reviewer's Remark</h3>
          <Input.TextArea value={reviewerRemark} onChange={(e) => setReviewerRemark(e.target.value)} rows={4} placeholder="Enter your remark here" />
        </Modal>

      </Styled.ContentWrapDiv>
    </Styled.MainDiv>
  );
};

export default ReviewerTable;